import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { CheckCircle, EmojiEvents } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import apiService from "../../service/Api";
import muzukuruLogo from "../../assets/Muzukuruimg3.png";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
type Day = (typeof DAYS)[number];

const SLIDE_DURATION_MS = 15_000;

type TodoRow = {
  task: string;
  cost?: string;
  who: string;
  day: Day;
  dueDetail?: string;
  done: boolean;
};

type CrewMember = {
  name: string;
  role: string;
  avatarUrl: string | null;
  starred?: boolean;
};

type CallSheetEntry = {
  id: number;
  status: "pending" | "accepted" | "cancelled";
  stream: {
    id: number;
    title: string;
    date: string;
    venue_name: string | null;
  };
  crew_member: {
    user: {
      first_name: string;
      last_name: string;
      profile_picture_url: string | null;
    };
  };
  roles: { name: string }[];
};

type EventCrew = {
  streamId: number;
  title: string;
  date: string;
  venue: string | null;
  members: CrewMember[];
};

function formatEventDate(isoDate: string): string {
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

type LeaderboardEntry = {
  name: string;
  streams: number;
};

type CrewAttendanceEntry = {
  rank: number;
  first_name: string;
  last_name: string;
  streams_attended: number;
};
type CrewAttendanceLeaderboardResponse = {
  month: string;
  total_participants: number;
  leaderboard: CrewAttendanceEntry[];
};

const AVATAR_COLORS = ["#2563eb", "#7c3aed", "#db2777", "#ea580c", "#16a34a", "#0891b2", "#4f46e5"];

function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] ?? "") + (parts.length > 1 ? parts[parts.length - 1][0] : "");
}

/** Up to 3 names from a "Foster & Chibaya" / "Cass, Foster & Conny" style string. */
function splitNames(who: string): string[] {
  return who
    .split(/,|&/)
    .map((name) => name.trim())
    .filter(Boolean);
}

const TODO_ROWS: TodoRow[] = [
  { task: "Collect Cams from Dirk", cost: "$60", who: "Foster & Chibaya", day: "Fri", dueDetail: "1500hrs", done: false },
  { task: "Collect Lenses from Chris", cost: "$50", who: "Jared Chibaya", day: "Fri", dueDetail: "1100hrs", done: true },
  { task: "Collect transmitters from Irvine", cost: "$50", who: "Cassie", day: "Fri", done: true },
  { task: "Collect Comms Kit from Prosper", cost: "$40", who: "Jared & Kait", day: "Fri", done: true },
  { task: "Return Starlink", cost: "$100", who: "Foster", day: "Thu", done: true },
  { task: "Site visit to Arcadia Baptist Church", who: "Cass, Foster & Conny", day: "Fri", dueDetail: "1030hrs", done: true },
];

const RANK_COLORS = ["#d97706", "#9ca3af", "#b45309"]; // gold, silver, bronze

const BORDER = "#e5e7eb";
const MUTED = "#6b7280";
const CARD_SHADOW = "0 1px 2px rgba(0,0,0,0.05)";

const Panel: React.FC<{
  title: string;
  subtitle?: string;
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
  sx?: object;
}> = ({ title, subtitle, headerExtra, children, sx }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
      boxShadow: CARD_SHADOW,
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      ...sx,
    }}
  >
    <Box sx={{ px: 2, py: 1.2, borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{title}</Typography>
        {headerExtra}
      </Box>
      {subtitle ? (
        <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.25 }}>{subtitle}</Typography>
      ) : null}
    </Box>
    <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 1 }}>{children}</Box>
  </Box>
);

const AvatarStack: React.FC<{ who: string }> = ({ who }) => {
  const names = splitNames(who);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ display: "flex" }}>
        {names.slice(0, 3).map((name, index) => (
          <Box
            key={name}
            title={name}
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              bgcolor: avatarColor(name),
              color: "#fff",
              fontSize: 10,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid #fff",
              ml: index === 0 ? 0 : -0.75,
            }}
          >
            {initials(name)}
          </Box>
        ))}
      </Box>
      <Typography sx={{ fontSize: 13, color: "#4b5563" }}>{who}</Typography>
    </Box>
  );
};


const CrewAvatar: React.FC<{ member: CrewMember; size: number }> = ({ member, size }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: "50%",
      flexShrink: 0,
      overflow: "hidden",
      bgcolor: avatarColor(member.name),
      color: "#fff",
      fontSize: size * 0.38,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {member.avatarUrl ? (
      <Box
        component="img"
        src={member.avatarUrl}
        alt=""
        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    ) : (
      initials(member.name)
    )}
  </Box>
);

/** One crew member, as a compact photo-forward card — part of a grid rather
 * than a list, so real faces are front and centre instead of just text. */
const CrewCard: React.FC<{ member: CrewMember }> = ({ member }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 0.4,
      p: 0.75,
      borderRadius: "10px",
      textAlign: "center",
      "&:hover": { bgcolor: "#f9fafb" },
    }}
  >
    <Box sx={{ position: "relative" }}>
      <CrewAvatar member={member} size={44} />
      {member.starred ? (
        <Box
          sx={{
            position: "absolute",
            bottom: -2,
            right: -2,
            fontSize: 11,
            color: "#d97706",
            bgcolor: "#fff",
            borderRadius: "50%",
            lineHeight: 1,
            width: 14,
            height: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 0 0 1.5px #fff",
          }}
        >
          ★
        </Box>
      ) : null}
    </Box>
    <Typography sx={{ fontSize: 11.5, lineHeight: 1.2, fontWeight: 600, color: "#111827", width: "100%" }} noWrap>
      {member.name}
    </Typography>
    <Typography
      sx={{
        fontSize: 9,
        lineHeight: 1.2,
        fontWeight: 600,
        color: MUTED,
        textTransform: "uppercase",
        letterSpacing: 0.3,
        width: "100%",
      }}
      noWrap
    >
      {member.role}
    </Typography>
  </Box>
);

/** Slide 1: today's equipment/site tasks + crew assignments (the original board). */
const TodayOpsSlide: React.FC<{ selectedDay: Day | null }> = ({ selectedDay }) => {
  const visibleRows = useMemo(
    () => (selectedDay ? TODO_ROWS.filter((row) => row.day === selectedDay) : TODO_ROWS),
    [selectedDay],
  );
  const doneCount = useMemo(() => visibleRows.filter((row) => row.done).length, [visibleRows]);
  const progressPct = visibleRows.length ? Math.round((doneCount / visibleRows.length) * 100) : 0;

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" }, gap: 2 }}>
      <Panel
        title="To Do"
        subtitle={selectedDay ? `Showing ${selectedDay}` : "Equipment collection & site tasks"}
        headerExtra={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 100, height: 6, borderRadius: "999px", bgcolor: "#f3f4f6", overflow: "hidden" }}>
              <Box
                sx={{
                  width: `${progressPct}%`,
                  height: "100%",
                  bgcolor: progressPct === 100 ? "#16a34a" : "#2563eb",
                  transition: "width 0.3s ease",
                }}
              />
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
              {doneCount}/{visibleRows.length}
            </Typography>
          </Box>
        }
        sx={{ minHeight: 0 }}
      >
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
          <Box component="thead">
            <Box component="tr">
              {[["task-col", "Task"], ["who-col", "Who"], ["due-col", "Due By"], ["done-col", ""]].map(([key, heading]) => (
                <Box
                  component="th"
                  key={key}
                  sx={{
                    textAlign: "left",
                    py: 1,
                    px: 1,
                    fontSize: 11,
                    color: MUTED,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    fontWeight: 600,
                  }}
                >
                  {heading}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {visibleRows.map((row) => (
              <Box component="tr" key={row.task} sx={{ "&:hover": { bgcolor: "#f9fafb" } }}>
                <Box
                  component="td"
                  sx={{
                    py: 1.8,
                    px: 1,
                    fontSize: 15,
                    fontWeight: 600,
                    color: "#111827",
                    textDecoration: row.done ? "line-through" : "none",
                  }}
                >
                  {row.task}
                  {row.cost ? (
                    <Typography
                      component="span"
                      sx={{
                        ml: 1,
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#92400e",
                        bgcolor: "#fef3c7",
                        px: 0.8,
                        py: 0.15,
                        borderRadius: "999px",
                        textDecoration: "none",
                        display: "inline-block",
                      }}
                    >
                      {row.cost}
                    </Typography>
                  ) : null}
                </Box>
                <Box component="td" sx={{ py: 1.8, px: 1 }}>
                  <AvatarStack who={row.who} />
                </Box>
                <Box component="td" sx={{ py: 1.8, px: 1, fontSize: 13, color: "#4b5563", whiteSpace: "nowrap" }}>
                  {row.day}{row.dueDetail ? ` · ${row.dueDetail}` : ""}
                </Box>
                <Box component="td" sx={{ py: 1.8, px: 1, textAlign: "right" }}>
                  {row.done ? (
                    <CheckCircle sx={{ fontSize: 18, color: "#16a34a" }} />
                  ) : (
                    <Box sx={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${BORDER}`, display: "inline-block" }} />
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Panel>

      <CrewAssignmentsColumn />
    </Box>
  );
};

const CREW_REFRESH_MS = 5 * 60_000;

/** Right-hand column of the Today's Ops slide: one panel per active event,
 * each listing its assigned crew + role. Wired to the real call-sheet API
 * (see call_sheet/serializers.py — CallSheetSerializer), grouped client-side
 * by stream since there's no "crew by event" endpoint yet. */
const CREW_EVENT_DURATION_MS = 6_000;

const CrewAssignmentsColumn: React.FC = () => {
  const [events, setEvents] = useState<EventCrew[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const fetchCallSheets = async () => {
      try {
        // page_size=10 is the API default (StandardResultsSetPagination) —
        // far too small here, since that's 10 call-sheet *rows*, not events,
        // and the default ordering is newest-created, not soonest-upcoming.
        // Override it; this board still does its own date filter/sort below.
        const res = await apiService.get<CallSheetEntry[] | { results: CallSheetEntry[] }>(
          "/api/call-sheets/callsheet/?page_size=200",
        );
        if (cancelled) return;
        const raw = Array.isArray(res.data) ? res.data : res.data?.results ?? [];

        const byStream = new Map<number, EventCrew>();
        for (const entry of raw) {
          if (entry.status === "cancelled") continue;
          const existing = byStream.get(entry.stream.id);
          const member: CrewMember = {
            name: `${entry.crew_member.user.first_name} ${entry.crew_member.user.last_name}`.trim(),
            role: entry.roles.map((role) => role.name).join(", ") || "Unassigned role",
            avatarUrl: entry.crew_member.user.profile_picture_url,
          };
          if (existing) {
            existing.members.push(member);
          } else {
            byStream.set(entry.stream.id, {
              streamId: entry.stream.id,
              title: entry.stream.title,
              date: entry.stream.date,
              venue: entry.stream.venue_name,
              members: [member],
            });
          }
        }
        const todayIso = new Date().toISOString().slice(0, 10);
        const upcoming = Array.from(byStream.values())
          .filter((event) => event.date >= todayIso)
          .sort((a, b) => a.date.localeCompare(b.date));
        setEvents(upcoming);
        setError(null);
      } catch {
        if (!cancelled) setError("Failed to load crew assignments.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCallSheets();
    const refresh = window.setInterval(fetchCallSheets, CREW_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(refresh);
    };
  }, []);

  const pageCount = Math.max(1, Math.ceil(events.length / 2));

  useEffect(() => {
    if (pageIndex >= pageCount) setPageIndex(0);
  }, [pageCount, pageIndex]);

  useEffect(() => {
    if (pageCount <= 1) return;
    const timer = window.setTimeout(() => {
      setPageIndex((index) => (index + 1) % pageCount);
    }, CREW_EVENT_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [pageIndex, pageCount]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minHeight: 0, width: "100%" }}>
        <Panel title="Crew Assignments" sx={{ flex: 1, minHeight: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        </Panel>
      </Box>
    );
  }

  if (error || events.length === 0) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minHeight: 0, width: "100%" }}>
        <Panel title="Crew Assignments" sx={{ flex: 1, minHeight: 0 }}>
          <Typography sx={{ fontSize: 13, color: error ? "#b91c1c" : MUTED, textAlign: "center", py: 3 }}>
            {error ?? "No active call sheets right now."}
          </Typography>
        </Panel>
      </Box>
    );
  }

  // A call sheet can carry up to 12 crew — no grid fits that alongside other
  // events without going tiny. Instead: upcoming events (soonest first) are
  // shown two at a time, stacked top/bottom, each with the full panel width
  // to work with; a small internal carousel pages through further pairs,
  // same auto-advance + dot-indicator pattern as the main slides.
  const pair = events.slice(pageIndex * 2, pageIndex * 2 + 2);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minHeight: 0, width: "100%" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minHeight: 0 }}>
        {pair.map((event) => (
          <Panel
            key={event.streamId}
            title={event.title}
            subtitle={[
              formatEventDate(event.date),
              event.venue,
              `${event.members.length} crew`,
            ]
              .filter(Boolean)
              .join(" · ")}
            sx={{ flex: 1, minHeight: 0 }}
          >
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0.5 }}>
              {event.members.map((member, index) => (
                <CrewCard key={`${member.name}-${member.role}-${index}`} member={member} />
              ))}
            </Box>
          </Panel>
        ))}
      </Box>

      {pageCount > 1 ? (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 0.75, flexShrink: 0 }}>
          {Array.from({ length: pageCount }).map((_, index) => (
            <Box
              key={index}
              component="button"
              onClick={() => setPageIndex(index)}
              aria-label={`Show call sheets ${index * 2 + 1}-${Math.min(index * 2 + 2, events.length)}`}
              sx={{
                width: index === pageIndex ? 18 : 6,
                height: 6,
                p: 0,
                borderRadius: "999px",
                border: "none",
                bgcolor: index === pageIndex ? "#111827" : "#d1d5db",
                cursor: "pointer",
                transition: "all 0.25s ease",
              }}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

const LEADERBOARD_REFRESH_MS = 5 * 60_000;

/** Slide 3: crew leaderboard / shoutouts — the "why look at the screen" slide.
 * Wired to the real, already-live attendance-leaderboard endpoint used
 * elsewhere in the Pixel Pilots dashboard (see StreamsLeaderboard.tsx). */
const CrewLeaderboardSlide: React.FC = () => {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchLeaderboard = async () => {
      try {
        const now = new Date();
        const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
        const res = await apiService.get<CrewAttendanceLeaderboardResponse>(
          `/api/management/dashboard/crew-attendance-leaderboard/?month=${month}`,
        );
        if (cancelled) return;
        const mapped = (res.data?.leaderboard ?? []).map((entry) => ({
          name: `${entry.first_name} ${entry.last_name}`.trim(),
          streams: entry.streams_attended,
        }));
        setEntries(mapped);
        setError(null);
      } catch {
        if (!cancelled) setError("Failed to load the crew leaderboard.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchLeaderboard();
    const refresh = window.setInterval(fetchLeaderboard, LEADERBOARD_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(refresh);
    };
  }, []);

  return (
  <Box sx={{ flex: 1, minHeight: 0 }}>
    <Panel title="Crew Leaderboard" subtitle="Most streams this month" sx={{ height: "100%" }}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Typography sx={{ fontSize: 14, color: "#b91c1c", textAlign: "center", py: 4 }}>{error}</Typography>
      ) : entries.length === 0 ? (
        <Typography sx={{ fontSize: 14, color: MUTED, textAlign: "center", py: 4 }}>
          No attendance recorded yet this month.
        </Typography>
      ) : (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {entries.map((entry, index) => {
          const rankColor = RANK_COLORS[index];
          return (
            <Box
              key={entry.name}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                py: 1.6,
                px: 2,
                borderRadius: "10px",
                bgcolor: index === 0 ? "#fffbeb" : "transparent",
                "&:hover": { bgcolor: "#f9fafb" },
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 14,
                  fontWeight: 800,
                  color: rankColor ? "#fff" : "#6b7280",
                  bgcolor: rankColor ?? "#f3f4f6",
                  flexShrink: 0,
                }}
              >
                {index < 3 ? <EmojiEvents sx={{ fontSize: 17 }} /> : index + 1}
              </Box>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  bgcolor: avatarColor(entry.name),
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {initials(entry.name)}
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#111827", flex: 1 }}>{entry.name}</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>{entry.streams}</Typography>
              <Typography sx={{ fontSize: 12, color: MUTED }}>streams</Typography>
            </Box>
          );
        })}
      </Box>
      )}
    </Panel>
  </Box>
  );
};

type SlideDef = { key: string; label: string };
const SLIDES: SlideDef[] = [
  { key: "today", label: "Today's Ops" },
  { key: "leaderboard", label: "Crew Leaderboard" },
];

const PixelPilotsBoard: React.FC = () => {
  const navigate = useNavigate();
  const [selectedDay, setSelectedDay] = useState<Day | null>(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSlideIndex((index) => (index + 1) % SLIDES.length);
    }, SLIDE_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [slideIndex]);

  useEffect(() => {
    const clock = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(clock);
  }, []);

  const activeSlide = SLIDES[slideIndex].key;
  const dateLabel = now.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
  const timeLabel = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100%",
        bgcolor: "#f5f5f4",
        color: "#111827",
        display: "flex",
        flexDirection: "column",
        p: { xs: 2, md: 3 },
        gap: 2,
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            component="button"
            type="button"
            aria-label="Go to app home"
            onClick={() => navigate("/app-home")}
            sx={{
              display: "flex",
              alignItems: "center",
              p: 0,
              border: 0,
              background: "none",
              cursor: "pointer",
            }}
          >
            <Box component="img" src={muzukuruLogo} alt="" sx={{ height: 50, width: "auto" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#111827", lineHeight: 1.2 }}>
              Muzukuru Streaming
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: MUTED, lineHeight: 1.2 }}>
              Ops Dashboard
            </Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#111827", fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}>
            {timeLabel}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: MUTED, lineHeight: 1.2 }}>
            {dateLabel}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {DAYS.map((day) => {
            const active = selectedDay === day;
            return (
              <Box
                key={day}
                component="button"
                onClick={() => setSelectedDay(active ? null : day)}
                sx={{
                  width: 34,
                  height: 34,
                  p: 0,
                  borderRadius: "50%",
                  border: `1px solid ${active ? "#111827" : BORDER}`,
                  bgcolor: active ? "#111827" : "#fff",
                  color: active ? "#fff" : "#374151",
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  outline: "none",
                  transition: "all 0.15s ease",
                  "&:hover": { borderColor: "#111827" },
                }}
              >
                {day[0]}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Always mounted, just hidden — conditionally mounting these tore down
          and rebuilt their data fetches (and timers) on every slide change,
          flashing a loading spinner every ~15s. Visibility toggles via CSS
          instead, so fetched data and refresh intervals persist underneath. */}
      <Box sx={{ flex: 1, minHeight: 0, display: activeSlide === "today" ? "flex" : "none", flexDirection: "column" }}>
        <TodayOpsSlide selectedDay={selectedDay} />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, display: activeSlide === "leaderboard" ? "flex" : "none", flexDirection: "column" }}>
        <CrewLeaderboardSlide />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", gap: 1, flexShrink: 0 }}>
        {SLIDES.map((slide, index) => (
          <Box
            key={slide.key}
            component="button"
            onClick={() => setSlideIndex(index)}
            aria-label={`Go to ${slide.label}`}
            sx={{
              width: index === slideIndex ? 24 : 8,
              height: 8,
              p: 0,
              borderRadius: "999px",
              border: "none",
              bgcolor: index === slideIndex ? "#111827" : "#d1d5db",
              cursor: "pointer",
              transition: "all 0.25s ease",
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default PixelPilotsBoard;
